import { useEffect, useState, useContext, useRef } from "react";
import { initializeApp } from "firebase/app";
import {
  getDownloadURL,
  getStorage,
  ref,
  getMetadata,
  uploadBytesResumable,
  deleteObject
} from "firebase/storage";
import { getAuth } from "firebase/auth";
import {
  Timestamp,
  arrayUnion,
  arrayRemove,
  deleteField,
  initializeFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot
} from "firebase/firestore";
import { getPerformance } from "firebase/performance";

import { useAuthState } from "react-firebase-hooks/auth";
import dayjs from "dayjs";
import useSWR, { useSWRConfig } from "swr";
import { isEqual } from "lodash";

import { DEFAULT_START_DATE } from "@aclymatepackages/constants";
import { isCountryUsa } from "@aclymatepackages/other-helpers";

import { isObjectEmpty, generateTempId } from "./otherHelpers";
import { getAccountId, getAccountCollection } from "./accountHelpers";
import { AdminDataContext } from "./contexts/adminDataContext";
import usePlatformLayoutContext from "./contexts/platformLayout";
import useSimulationContext from "./contexts/simulationContext";
import { PermissionsContext } from "./contexts/permissionsContext";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

const app = initializeApp(firebaseConfig);
export const storage = getStorage(app);
export const db = initializeFirestore(app, { ignoreUndefinedProperties: true });
export const auth = getAuth(app);
export const fetchAuth = () => auth || getAuth(app);

getPerformance(app);

const adminDataCollections = [
  "offices",
  "employees",
  "vehicles",
  "offsets",
  "offset-subscriptions",
  "events",
  "vendors",
  "tasks",
  "audit-logs",
  "vendor-relationships",
  "all-aggregated-transactions",
  "matched-vendors-aggregated-transactions",
  "employees-aggregated-transactions",
  "events-aggregated-transactions"
];

const nonCompanyCollectionNames = {
  events: "events",
  employees: "individuals",
  "vendor-relationships": "vendor-relationships",
  "audit-logs": "audit-logs",
  "product-components": "product-components",
  "product-lifecycle-events": "product-lifecycle-events",
  customers: "vendor-relationships",
  "vendor-assigned-products": "product-components",
  "vendor-assigned-product-lifecycle-events": "product-lifecycle-events"
};

export const useAuth = () => useAuthState(getAuth());

export const signOut = async () => await getAuth().signOut();

const buildDocRef = (collectionName, docId) => {
  const accountsCollection = getAccountCollection();
  const accountId = getAccountId();

  if (!collectionName) {
    return doc(db, `${accountsCollection}/${accountId}`);
  }

  if (collectionName === "transactions") {
    return doc(db, `transactions/${docId}`);
  }

  if (Object.keys(nonCompanyCollectionNames).includes(collectionName)) {
    return doc(db, `${nonCompanyCollectionNames[collectionName]}/${docId}`);
  }

  return doc(
    db,
    `${accountsCollection}/${accountId}/${collectionName}/${docId}`
  );
};

const buildCollectionRef = (collectionName) => {
  if (collectionName === "transactions") {
    return collection(db, "transactions");
  }

  if (Object.keys(nonCompanyCollectionNames).includes(collectionName)) {
    const collectionPath = nonCompanyCollectionNames[collectionName];
    return collection(db, collectionPath);
  }

  const accountsCollection = getAccountCollection();
  const accountId = getAccountId();

  return collection(db, `${accountsCollection}/${accountId}/${collectionName}`);
};

const buildCollectionQuery = (collectionName, queries = []) => {
  const accountId = getAccountId();

  const collectionQueries = {
    employees: [["currentLinkedIds", "array-contains", accountId]],
    events: [["companyIds", "array-contains", accountId]],
    "vendor-relationships": [["buyerId", "==", accountId]],
    transactions: [["buyerId", "==", accountId]],
    "product-components": [["buyerIds", "array-contains", accountId]],
    "product-lifecycle-events": [["ownerId", "==", accountId]],
    customers: [["vendorId", "==", accountId]],
    "vendor-assigned-products": [["vendorIds", "array-contains", accountId]],
    "vendor-assigned-product-lifecycle-events": [["vendor.id", "==", accountId]]
  };

  const archivedQuery =
    collectionName === "vehicles" ||
    collectionName === "transactions" ||
    collectionName === "vendor-relationships" ||
    collectionName === "customers" ||
    collectionName === "product-components" ||
    collectionName === "vendor-assigned-products" ||
    collectionName === "action-items"
      ? [["archived", "==", false]]
      : [];

  const additionalQueries = collectionQueries[collectionName] || [];

  const queriesArray = [...additionalQueries, ...archivedQuery, ...queries].map(
    (queryArray) => where(...queryArray)
  );

  const collectionRef = buildCollectionRef(collectionName);

  return query(collectionRef, ...queriesArray);
};

export const fetchCollectionRefQuery = async (collectionName, queries = []) => {
  const firebaseQuery = buildCollectionQuery(collectionName, queries);

  return await getDocs(firebaseQuery)
    .then((rawData) => {
      return rawData.docs.map((doc) => ({
        ...unpackFirebaseObj(doc.data()),
        id: doc.id
      }));
    })
    .catch((e) => {
      // console.log(`error fetching ${collectionName} documents: ` + e);
      throw new Error(e);
    });
};

const dateToFirebaseTimeStamp = (date = null) => {
  // Validate that the date is not null and represents a valid date
  if (!date || (date instanceof Date && isNaN(date.getTime()))) {
    console.warn("Invalid date passed to dateToFirebaseTimeStamp:", date);
    return null;
  }

  const unixTimestamp = dayjs(date).unix();

  // Check if dayjs conversion resulted in a valid timestamp
  if (isNaN(unixTimestamp)) {
    console.warn("dayjs conversion resulted in NaN for date:", date);
    return null;
  }

  return new Timestamp(unixTimestamp, 0);
};

export const firebaseArrayUnionObj = (data) => ({ data, arrayUnion: true });

const firebaseArrayUnion = (value) => {
  if (Array.isArray(value)) {
    return arrayUnion(...value);
  }
  return arrayUnion(value);
};

export const firebaseArrayRemoveObj = (data) => ({ data, arrayRemove: true });

export const firebaseArrayRemove = (value) => arrayRemove(value);

export const firebaseFieldDeleteObj = () => ({ deletable: true });

export const prepareFirebaseObj = (obj) =>
  Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => !(value === null || value === undefined))
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [
            key,
            value.map((row) =>
              typeof row === "string" || typeof row === "number"
                ? row
                : prepareFirebaseObj(row)
            )
          ];
        }

        if (typeof value === "string" && !isNaN(value)) {
          return [key, Number(value)];
        }

        if (
          typeof key === "string" &&
          typeof value === "string" &&
          RegExp(/^\d{1,2}(\/|-)\d{1,2}(\/|-)(?:\d{4}|\d{2})$/, "g").test(
            value
          ) &&
          !isObjectEmpty(value)
        ) {
          const timestamp = dateToFirebaseTimeStamp(new Date(value));
          return timestamp ? [key, timestamp] : [key, null];
        }

        if (value && typeof value === "object") {
          if (dayjs.isDayjs(value)) {
            const convertedToDateObj = dayjs(value).toDate();
            const timestamp = dateToFirebaseTimeStamp(convertedToDateObj);
            return timestamp ? [key, timestamp] : [key, null];
          }

          if (Object.prototype.toString.call(value) === "[object Date]") {
            // Validate that the Date object represents a valid date
            if (isNaN(value.getTime())) {
              console.warn(
                "Invalid Date object found in prepareFirebaseObj:",
                key,
                value
              );
              return [key, null];
            }
            const timestamp = dateToFirebaseTimeStamp(dayjs(value).toDate());
            return timestamp ? [key, timestamp] : [key, null];
          }

          const { seconds, data, arrayUnion, arrayRemove, deletable } = value;

          if (seconds) {
            return [key, value];
          }
          if (arrayUnion && data) {
            return [key, firebaseArrayUnion(data)];
          }

          if (arrayRemove && data) {
            return [key, firebaseArrayRemove(data)];
          }

          if (deletable) {
            return [key, deleteField()];
          }

          return [key, prepareFirebaseObj(value)];
        }

        return [key, value];
      })
  );

export const unpackFirebaseObj = (obj) =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (Array.isArray(value)) {
        return [
          key,
          value.map((row) =>
            typeof row === "string" || typeof row === "number"
              ? row
              : unpackFirebaseObj(row)
          )
        ];
      }
      if (value && typeof value === "object") {
        const { seconds, _seconds } = value || {};
        if (seconds || _seconds) {
          return [key, new Date((seconds || _seconds) * 1000)];
        }
        return [key, unpackFirebaseObj(value)];
      }
      return [key, value];
    })
  );

export const useDestructuredSWRdata = (queryCache, fetcher) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { data: rawData, error: rawError } = useSWR(queryCache, fetcher);

  useEffect(() => {
    if (!rawError && (!isObjectEmpty(rawData) || Array.isArray(rawData))) {
      setData(rawData);
      setLoading(false);
    }
    if (rawError) {
      setError(rawError);
      setLoading(false);
    }
  }, [rawData, rawError]);

  return [data, loading, error];
};

const fetchAccountData = async () =>
  await getDoc(buildDocRef())
    .then((doc) => unpackFirebaseObj({ ...doc.data(), id: doc.id }))
    .catch((error) => {
      // console.log("account data error", error);
      throw new Error(error);
    });

export const useAccountData = () => {
  const { viewMode } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  const accountDataFromSWR = useDestructuredSWRdata(
    "accountData",
    () => fetchAccountData(),
    {}
  );

  const insertDefaultCompanyParams = (companyData) => {
    const { startDate, measurementSystem, name, branding, address, isRemote } =
      companyData || {};
    const { companyStatement } = branding || {};

    const defaultCompanyStatement =
      branding?.companyStatement ||
      `${name} is using Aclymate to measure, reduce, report and offset its carbon footprint. The company is doing this because it's committed to being transparent about its carbon footprint to all internal and external stakeholders.`;

    const defaultMeasurementSystem = isCountryUsa(address?.country)
      ? "imperial"
      : "metric";

    return {
      ...companyData,
      isRemote: isRemote || false,
      startDate: startDate || DEFAULT_START_DATE,
      measurementSystem: measurementSystem || defaultMeasurementSystem,
      branding: {
        ...branding,
        companyStatement: companyStatement || defaultCompanyStatement
      }
    };
  };

  if (viewMode === "simulation") {
    return [simulationContext.accountData || {}, false];
  }

  if (viewMode === "admin") {
    const { companyData } = adminData;

    return [insertDefaultCompanyParams(companyData), isAdminDataLoading];
  }

  const [accountData, ...otherProps] = accountDataFromSWR;

  return [
    insertDefaultCompanyParams(Array.isArray(accountData) ? {} : accountData),
    ...otherProps
  ];
};

const mutateNestedData = (existingObj, updateObj) =>
  Object.fromEntries(
    Object.entries(updateObj).map(([key, value]) => {
      if (!key.includes(".")) {
        return [key, value];
      }
      const keys = key.split(".");
      const existingData = existingObj[keys[0]];
      return [keys[0], { ...existingData, [keys[1]]: value }];
    })
  );

export const useCollectionDataListener = (collection, queries = []) => {
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  const [dbData, setDbData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const dbDataRef = useRef(dbData);
  const dataLoadingRef = useRef(dataLoading);

  // Track the latest queries in a ref so the effect always uses original values
  // (including Date objects) rather than JSON-parsed strings.
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  // Serialize queries for change-detection. Date objects are serialized with a
  // type tag so they round-trip correctly for comparison purposes, without
  // converting them to plain strings that Firestore can't use in Timestamp queries.
  const queriesKey = JSON.stringify(queries, (_key, value) => {
    if (value instanceof Date) {
      return { __type: "Date", value: value.toISOString() };
    }
    return value;
  });

  useEffect(() => {
    const syncData = () => {
      if (isSimulation) {
        dataLoadingRef.current = false;
        setDataLoading(false);
        return;
      }

      const accountId = getAccountId();
      const isCompanyScoped =
        !Object.keys(nonCompanyCollectionNames).includes(collection) &&
        collection !== "transactions";
      if (isCompanyScoped && !accountId) {
        dataLoadingRef.current = false;
        setDataLoading(false);
        return;
      }

      const firebaseQuery = buildCollectionQuery(
        collection,
        queriesRef.current
      );
      const unsubscribe = onSnapshot(
        firebaseQuery,
        (snap) => {
          const data = snap.docs.map((doc) => ({
            ...unpackFirebaseObj(doc.data()),
            id: doc.id
          }));

          if (dataLoadingRef.current || !isEqual(data, dbDataRef.current)) {
            dbDataRef.current = data;
            dataLoadingRef.current = false;
            setDbData(data);
            setDataLoading(false);
          }
        },
        () => {
          dataLoadingRef.current = false;
          setDataLoading(false);
        }
      );

      return () => unsubscribe();
    };

    syncData();
  }, [collection, queriesKey, isSimulation]);

  if (viewMode === "admin" && adminDataCollections.includes(collection)) {
    return [adminData[collection], isAdminDataLoading];
  }

  if (isSimulation) {
    const simData = simulationContext[collection] || [];
    return [simData, false];
  }

  return [dbData, dataLoading];
};

export const useCachedDisplayData = (collection, queries = []) => {
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData = {}, isAdminDataLoading } =
    useContext(AdminDataContext) || {};
  const simulationContext = useSimulationContext() || {};

  const [dbCollection, dbCollectionLoading] = useDestructuredSWRdata(
    collection,
    () => fetchCollectionRefQuery(collection, queries)
  );

  if (isSimulation) {
    const simData = simulationContext[collection] || [];
    return [simData, false];
  }

  if (viewMode === "admin" && adminDataCollections.includes(collection)) {
    return [adminData[collection], isAdminDataLoading];
  }

  return [dbCollection, dbCollectionLoading];
};

const prepareUpdateCacheObj = (updateObj, existingDoc = {}) =>
  Object.fromEntries(
    Object.entries(updateObj)
      .filter(
        ([_, value]) =>
          !(value && typeof value === "object" && value.deletable === true)
      )
      .map(([key, value]) => {
        if (Array.isArray(value)) {
          return [
            key,
            value.map((row) =>
              typeof row === "string" || typeof row === "number"
                ? row
                : prepareUpdateCacheObj(row)
            )
          ];
        }

        if (value && typeof value === "object") {
          const { data, arrayUnion, arrayRemove } = value;
          if (!arrayUnion && !arrayRemove) {
            return [key, value];
          }

          const existingField = existingDoc[key];
          if (arrayUnion && Array.isArray(existingField)) {
            return [key, [...existingField, data]];
          }

          if (arrayRemove && Array.isArray(existingField)) {
            const newArray = existingField.filter(({ id }) => id === data.id);
            return [key, newArray];
          }
        }

        return [key, value];
      })
  );

const addDocRef = async (collection, data, userId) => {
  const collectionRef = buildCollectionRef(collection);
  const accountId = getAccountId();

  const dataWithBuyerId =
    collection === "transactions"
      ? { ...data, buyerId: accountId, isAggregated: false }
      : data;

  const preparedFirebaseObj = prepareFirebaseObj({
    ...dataWithBuyerId,
    _updatedBy: userId || "system"
  });

  return await addDoc(collectionRef, preparedFirebaseObj).then(
    (docRef) => docRef.id
  );
};

const updateDocRef = async ({ collection, docId, updateObj, userId }) => {
  const getDocRef = () => {
    if (collection && docId) {
      return buildDocRef(collection, docId);
    }

    return buildDocRef();
  };

  const docRef = getDocRef();
  const updateFirebaseObj = prepareFirebaseObj({
    ...updateObj,
    _updatedBy: userId || "system"
  });

  return await updateDoc(docRef, updateFirebaseObj);
};

const deleteDocRef = async (collection, docId) => {
  const docRef = buildDocRef(collection, docId);
  return await deleteDoc(docRef);
};

export const useCachedFirebaseCrud = () => {
  const { mutate } = useSWRConfig();
  const { viewMode, isSimulation } = usePlatformLayoutContext() || {};
  const { adminData, editAdminData } = useContext(AdminDataContext) || {};
  const [user] = useAuth();
  const { uid: userId } = user || {};

  const { updateCollection } = useSimulationContext() || {};
  const { canEdit } = useContext(PermissionsContext);

  const viewModeDataObj = {
    admin: {
      viewModeData: adminData,
      editViewModeData: editAdminData
    }
  };
  const { viewModeData, editViewModeData } = viewModeDataObj[viewMode] || {};
  const isViewModeNotCompany = !!viewMode && viewMode !== "company";

  const modifyViewModeData = (dbCollection, modificationFunction) => {
    const currentViewModeData = viewModeData[dbCollection];
    const newViewModeData = modificationFunction(currentViewModeData);

    return editViewModeData(collection, newViewModeData);
  };

  const updateAccountData = async (updateObj) => {
    const updater = (companyData) =>
      prepareUpdateCacheObj(
        {
          ...companyData,
          ...mutateNestedData(companyData, updateObj)
        },
        companyData
      );

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection("accountData", updater);
      return Promise.resolve();
    }

    mutate("accountData", updater, false);

    await updateDocRef({
      updateObj,
      userId
    });

    return mutate("accountData");
  };

  const updateCollectionDoc = async (collection, docId, updateObj) => {
    const updateLocalData = (currentData) => {
      if (!currentData) {
        return;
      }

      return currentData.map((doc) => {
        if (docId === doc.id) {
          const preparedObj = prepareUpdateCacheObj(updateObj, doc);

          const deletableKeys = Object.keys(updateObj).filter(
            (k) => updateObj[k]?.deletable === true
          );
          const baseDoc = deletableKeys.length
            ? Object.fromEntries(
                Object.entries(doc).filter(([k]) => !deletableKeys.includes(k))
              )
            : doc;

          return { ...baseDoc, ...preparedObj };
        }
        return doc;
      });
    };

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection(collection, updateLocalData);
      return Promise.resolve();
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, updateLocalData);
    }

    mutate(collection, updateLocalData, false);

    return await updateDocRef({
      collection,
      docId,
      updateObj,
      userId
    })
      .then(() => mutate(collection))
      .catch((e) => {
        console.log("firebase update error: " + JSON.stringify(e));
        mutate(collection);
        throw new Error(e);
      });
  };

  const newCollectionDoc = async (collection, data) => {
    const tempId = generateTempId();

    const addLocalData = (currentCollection = []) => {
      const newObj = isSimulation && data?.id ? data : { ...data, id: tempId };
      return [...currentCollection, newObj];
    };

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isSimulation) {
      updateCollection(collection, addLocalData);
      return Promise.resolve(tempId);
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, addLocalData);
    }

    mutate(collection, addLocalData, false);

    const docId = await addDocRef(collection, data, userId);

    mutate(collection);
    return docId;
  };

  const deleteCachedCollectionDoc = async (collection, docId) => {
    const deleteLocalData = (currentCollection = []) =>
      currentCollection.filter(({ id }) => id !== docId);

    if (!canEdit) {
      return Promise.resolve();
    }

    if (isViewModeNotCompany) {
      return modifyViewModeData(collection, deleteLocalData);
    }

    mutate(
      collection,
      (currentCollection) =>
        currentCollection && currentCollection.filter(({ id }) => id !== docId),
      false
    );

    return await deleteDocRef(collection, docId).then(() => {
      mutate(collection);
    });
  };

  return {
    updateCollectionDoc,
    updateAccountData,
    newCollectionDoc,
    deleteCachedCollectionDoc
  };
};

const checkStorageFileExists = async (storageRef) => {
  try {
    await getDownloadURL(storageRef);
    return true;
  } catch (e) {
    return false;
  }
};

export const uploadFileToStorage = async ({
  path,
  fileObject,
  fileMetadata,
  hasUserConfirmedUpload
}) => {
  const storageRef = ref(storage, path);

  if (!hasUserConfirmedUpload) {
    const isDuplicateFile = await checkStorageFileExists(storageRef);
    if (isDuplicateFile) {
      return { isDuplicateFileDetected: true };
    }
  }

  const uploadTask = uploadBytesResumable(storageRef, fileObject, fileMetadata);

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => {
        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        );

        console.log("progress", progress);
      },
      (error) => {
        reject(error);
      },
      () => {
        getDownloadURL(uploadTask.snapshot.ref)
          .then((url) => {
            resolve(url);
          })
          .catch(reject);
      }
    );
  });
};

export const getFileMetadata = async (storagePath, bucketName = null) => {
  try {
    // Use custom bucket if provided, otherwise use default storage
    const storageInstance = bucketName
      ? getStorage(app, `gs://${bucketName}`)
      : storage;

    const storageRef = ref(storageInstance, storagePath);
    const metadata = await getMetadata(storageRef);

    return metadata;
  } catch (error) {
    console.error("Error getting file metadata:", error);
    throw error;
  }
};

export const deleteFileFromStorage = async (path, bucketName = null) => {
  try {
    // Use custom bucket if provided, otherwise use default storage
    const storageInstance = bucketName
      ? getStorage(app, `gs://${bucketName}`)
      : storage;

    const storageRef = ref(storageInstance, path);

    await deleteObject(storageRef);

    return { success: true };
  } catch (error) {
    console.error("Error deleting file from storage:", error);
    throw error;
  }
};

export const getTaskFileDownloadUrl = async (fileBucket, filePath) => {
  const storage = getStorage(app, `gs://${fileBucket}`);
  const fileRef = ref(storage, filePath);

  return await getDownloadURL(fileRef);
};
